/**
 * Restructures Swym wishlist rows to match DS cart drawer line items (no qty / remove row).
 */
const DS_SWYM_ITEM_SELECTOR = 'swym-storefront-layout-item:not(.swym-storefront-layout-loader)';
const DS_SWYM_ITEMS_CONTAINER_SELECTOR =
  '#swym-storefront-layout-items-container, #swym-storefront-layout-sfl-items-container';

const DS_SWYM_SIZE_LABEL = 'Taglia';

/**
 * @returns {Array<object>}
 */
function collectSwymListContents() {
  const ctx = window.SwymStorefrontLayoutContext;
  const items = [];

  if (!ctx) {
    return items;
  }

  const addFromList = (list) => {
    if (Array.isArray(list?.listcontents)) {
      items.push(...list.listcontents);
    }
  };

  addFromList(ctx.DefaultList);

  for (const list of ctx.allLists || []) {
    addFromList(list);
  }

  for (const list of ctx.lists || []) {
    addFromList(list);
  }

  if (Array.isArray(ctx.sflListItems)) {
    items.push(...ctx.sflListItems);
  }

  return items;
}

/**
 * @param {string | undefined} empi
 * @param {string | undefined} epi
 * @returns {object | undefined}
 */
function findSwymItemData(empi, epi) {
  if (!empi || !epi) {
    return undefined;
  }

  const normalizedEmpi = String(empi);
  const normalizedEpi = String(epi);

  return collectSwymListContents().find(
    (entry) => String(entry.empi) === normalizedEmpi && String(entry.epi) === normalizedEpi
  );
}

/**
 * @param {object | undefined} dataItem
 * @param {string | undefined} epi
 * @returns {object | undefined}
 */
function getSelectedVariant(dataItem, epi) {
  const ctx = window.SwymStorefrontLayoutContext;
  const variantId = Number(epi);

  if (ctx?.getSelectedVariant && dataItem) {
    return ctx.getSelectedVariant(dataItem, variantId);
  }

  return dataItem?.productData?.variants?.find((variant) => String(variant.id) === String(epi));
}

/**
 * @param {object | undefined} productData
 * @param {object | undefined} selectedVariant
 * @returns {string}
 */
function formatVariantLine(productData, selectedVariant) {
  if (!productData?.options?.length || !selectedVariant) {
    return '';
  }

  const sizeParts = [];
  const otherParts = [];

  for (let index = 0; index < productData.options.length; index += 1) {
    const option = productData.options[index];
    const value = selectedVariant[`option${index + 1}`];

    if (!value || value === 'Default Title') {
      continue;
    }

    const optionName = (option.name || '').toLowerCase();
    const isSize =
      optionName.includes('taglia') ||
      optionName.includes('size') ||
      optionName.includes('numero') ||
      optionName.includes('größe') ||
      optionName.includes('groesse');

    if (isSize) {
      sizeParts.push(`${DS_SWYM_SIZE_LABEL} ${value}`);
    } else {
      otherParts.push(value);
    }
  }

  return [...sizeParts, ...otherParts].join(' / ');
}

/**
 * @param {Element} itemEl
 */
function enhanceSwymLineItem(itemEl) {
  if (!(itemEl instanceof HTMLElement)) {
    return;
  }

  const gridItem = itemEl.querySelector('.swym-storefront-layout-grid-item');

  if (!gridItem || gridItem.classList.contains('ds-swym-grid-item--structured')) {
    return;
  }

  const content = itemEl.querySelector('.swym-storefront-layout-grid-item-content');
  const titleLink = content?.querySelector('.swym-storefront-layout-grid-item-title');
  const priceBlock = content?.querySelector('.swym-storefront-layout-grid-item-price-variant');

  if (!content || !titleLink || !priceBlock) {
    return;
  }

  const dataItem = findSwymItemData(itemEl.dataset.empi, itemEl.dataset.epi);
  const selectedVariant = getSelectedVariant(dataItem, itemEl.dataset.epi);

  const details = document.createElement('div');
  details.className = 'ds-swym-grid-item-details';

  const vendor = dataItem?.productData?.vendor?.trim();

  if (vendor) {
    const vendorEl = document.createElement('p');
    vendorEl.className = 'ds-swym-grid-item-vendor';
    vendorEl.textContent = vendor.toUpperCase();
    details.append(vendorEl);
  }

  const titleWrap = document.createElement('p');
  titleWrap.className = 'ds-swym-grid-item-title-wrap';
  titleWrap.append(titleLink);
  details.append(titleWrap);

  const variantLine = formatVariantLine(dataItem?.productData, selectedVariant);

  if (variantLine) {
    const variantsEl = document.createElement('p');
    variantsEl.className = 'ds-swym-grid-item-variants';
    variantsEl.textContent = variantLine;
    details.append(variantsEl);
  }

  const priceCol = document.createElement('div');
  priceCol.className = 'ds-swym-grid-item-price';

  const finalPrice = priceBlock.querySelector('.swym-storefront-layout-grid-item-final-price');
  const comparePrice = priceBlock.querySelector('.swym-storefront-layout-grid-item-compare-price');

  if (finalPrice) {
    priceCol.append(finalPrice);
  }

  if (comparePrice) {
    priceCol.append(comparePrice);
  }

  priceBlock.remove();

  const actionContainer = content.querySelector('.swym-storefront-layout-grid-item-action-container');

  if (actionContainer) {
    actionContainer.classList.add('ds-swym-grid-item-actions--hidden');
  }

  content.replaceChildren(details);

  if (actionContainer) {
    content.append(actionContainer);
  }

  gridItem.insertBefore(priceCol, content.nextSibling);
  gridItem.classList.add('ds-swym-grid-item--structured');
}

function enhanceAllSwymLineItems(root = document) {
  for (const itemEl of root.querySelectorAll(DS_SWYM_ITEM_SELECTOR)) {
    enhanceSwymLineItem(itemEl);
  }
}

let enhanceFrame = 0;

function scheduleEnhance(root = document) {
  if (enhanceFrame) {
    cancelAnimationFrame(enhanceFrame);
  }

  enhanceFrame = requestAnimationFrame(() => {
    enhanceFrame = 0;
    enhanceAllSwymLineItems(root);
  });
}

function observeSwymItemLists() {
  for (const container of document.querySelectorAll(DS_SWYM_ITEMS_CONTAINER_SELECTOR)) {
    if (!(container instanceof HTMLElement) || container.dataset.dsSwymItemsObserved === 'true') {
      continue;
    }

    container.dataset.dsSwymItemsObserved = 'true';
    scheduleEnhance(container);

    const observer = new MutationObserver(() => {
      scheduleEnhance(container);
    });

    observer.observe(container, { childList: true, subtree: true });
  }
}

function initDsSwymStorefrontItems() {
  scheduleEnhance();
  observeSwymItemLists();

  document.addEventListener('sw:renderVS', () => {
    scheduleEnhance();
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDsSwymStorefrontItems, { once: true });
} else {
  initDsSwymStorefrontItems();
}
